/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { UserModal } from '../../components/user/UserModal';
import { UserService } from '../../services/user.service';
import { InvitationTableHeader } from '../../components/invite/InvitationTableHeader';
import { InvitationRow } from '../../components/invite/InvitationRow';

export const InvitationsPage = () => {
  const [invitations, setInvitations] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState<Record<string, 'resend' | 'revoke' | null>>({});

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await UserService.getAllInvitations();
      setInvitations(res.data);
    } catch (err) {
      console.error('[InvitationsPage] Failed to fetch invitations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => setIsModalOpen(false);

  const updateSingleInvitation = async (id: string) => {
    try {
      const res = await UserService.getInvitationById(id);
      setInvitations((prev) => {
        const filtered = prev.filter((inv) => inv.id !== id);
        return [res.data, ...filtered];
      });
    } catch (err) {
      console.error(`[InvitationsPage] Failed to fetch updated invitation ${id}:`, err);
    }
  };

  const handleResend = async (id: string) => {
  setLoadingInvites((prev) => ({ ...prev, [id]: 'resend' }));
  try {
    const res = await UserService.resendInvitation(id);
    const newInvite = res.data;

    setInvitations((prev) => {
      const filtered = prev.filter((inv) => inv.id !== id);
      return [newInvite, ...filtered];
    });

  } catch (err) {
    console.error(`[InvitationsPage] Failed to resend invitation for id: ${id}`, err);
  } finally {
    setLoadingInvites((prev) => ({ ...prev, [id]: null }));
  }
};


  const handleRevoke = async (id: string) => {
    setLoadingInvites((prev) => ({ ...prev, [id]: 'revoke' }));
    try {
      await UserService.revokeInvitation(id);
      await updateSingleInvitation(id);
    } catch (err) {
      console.error(`[InvitationsPage] Failed to revoke invitation for id: ${id}`, err);
    } finally {
      setLoadingInvites((prev) => ({ ...prev, [id]: null }));
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <h1 className="text-4xl font-bold text-center sm:pb-4 sm:text-left text-primary">Invitations</h1>

      <div className="flex justify-center sm:justify-start">
        <Button
          icon={<Plus />}
          className="px-16 py-3 mt-4 sm:px-6 sm:mt-0"
          onClick={() => setIsModalOpen(true)}
        >
          New
        </Button>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <p className="mt-6 text-xl text-center text-muted-foreground text-primary">Loading...</p>
        ) : invitations.length === 0 ? (
          <p className="mt-6 text-xl text-center text-primary text-muted-foreground">
            No invitations found.
          </p>
        ) : (
          <>
            <InvitationTableHeader isSuperAdmin={user?.role?.name === 'SUPER_ADMIN'} />
            {invitations.map((inv) => (
              <InvitationRow
                key={inv.id}
                invitation={inv}
                isSuperAdmin={user?.role?.name === 'SUPER_ADMIN'}
                onResend={() => handleResend(inv.id)}
                onRevoke={() => handleRevoke(inv.id)}
                loadingAction={loadingInvites[inv.id]}
              />
            ))}
          </>
        )}
      </div>

      <UserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={() => {
          load();
          handleModalClose();
        }}
        lockedCompanyId={user?.role?.name === 'COMPANY_ADMIN' ? user?.company?.id : undefined}
      />
    </div>
  );
};
